
import React, { useState } from 'react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = 'var(--orange)';
const P  = ['#fb923c','#22d3ee','#4ade80','#a78bfa','#fbbf24','#f87171'];

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=0.01,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?3:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);
const Sel = ({label,value,onChange,options}) => (
  <div style={{marginBottom:'8px'}}>
    {label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'3px'}}>{label}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'6px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  </div>
);

// ─── MATH ─────────────────────────────────────────────────────────────────────
const randn = () => { let u=0,v=0; while(!u)u=Math.random(); while(!v)v=Math.random(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };

const LR_SCHEDULES = {
  constant:    (ep,lr0,T)=>lr0,
  step:        (ep,lr0,T)=>lr0*Math.pow(0.5,Math.floor(ep/(T/4))),
  cosine:      (ep,lr0,T)=>lr0*0.5*(1+Math.cos(Math.PI*ep/T)),
  warmup_cos:  (ep,lr0,T)=>{ const w=Math.floor(T*0.1); return ep<w?lr0*ep/w:lr0*0.5*(1+Math.cos(Math.PI*(ep-w)/(T-w))); },
  exponential: (ep,lr0,T)=>lr0*Math.pow(0.97,ep),
  cyclic:      (ep,lr0,T)=>{ const cycle=Math.floor(T/4); const x=Math.abs(ep%cycle-(cycle/2))/(cycle/2); return lr0*(0.1+0.9*(1-x)); },
  one_cycle:   (ep,lr0,T)=>{ const p=ep/T; return p<0.3?lr0*0.1+lr0*0.9*(p/0.3):p<0.7?lr0*(1-(p-0.3)/0.4*0.8):lr0*0.2*(1-(p-0.7)/0.3); },
};

const simTraining = ({lr0,epochs,batchSize,sched,optimizer,noise=1,warmup=false}) => {
  const pts=[]; let loss=2.0+randn()*0.1, valLoss=2.2+randn()*0.1;
  const schedFn = LR_SCHEDULES[sched]||LR_SCHEDULES.constant;
  const optMult = optimizer==='adam'?1.4:optimizer==='sgd_m'?1.1:optimizer==='rmsprop'?1.2:1.0;
  const batchMult = Math.sqrt(32/batchSize);
  let gradNorm=1.0;

  for(let e=0;e<epochs;e++){
    const curLr=schedFn(e,lr0,epochs);
    const decay=0.04*curLr*optMult*(1/batchMult);
    const noiseAmt=(batchMult*0.015+0.005)*noise;
    gradNorm=Math.max(0.01,gradNorm*(1-decay*0.3)+randn()*0.1);

    loss=Math.max(0.02,loss*(1-decay)+randn()*noiseAmt);
    valLoss=Math.max(0.03,valLoss*(1-decay*0.7)+Math.abs(randn()*noiseAmt*1.3)+loss*0.001*e*0.01);

    if(e%Math.max(1,Math.floor(epochs/100))===0){
      pts.push({epoch:e,loss:+loss.toFixed(4),val:+valLoss.toFixed(4),lr:+curLr.toFixed(6),gradNorm:+gradNorm.toFixed(4)});
    }
  }
  return pts;
};

// Loss landscape for a 1D slice
const lossLandscape = (paramRange=[-3,3], pts=100, sharpness=1) => {
  const step=(paramRange[1]-paramRange[0])/pts;
  return Array.from({length:pts},(_,i)=>{
    const x=paramRange[0]+i*step;
    const loss=0.1+Math.pow(x,2)*0.3*sharpness+0.2*Math.sin(x*3)*sharpness+randn()*0.01;
    return {x:+x.toFixed(3),loss:+Math.max(0,loss).toFixed(4)};
  });
};

const TABS=['loss-curves','lr-schedule','landscape','batch-effects','gradient-health','compare'];

// ─── LOSS LANDSCAPE SVG ──────────────────────────────────────────────────────
function LossLandscapeSVG({data, paths, sharpness}) {
  if(!data?.length) return null;
  const W=680, H=220, PAD=30;
  const mn=Math.min(...data.map(d=>d.loss)), mx=Math.max(...data.map(d=>d.loss));
  const xR=data.at(-1).x-data[0].x, yR=mx-mn||1;
  const toSVG=(x,y)=>[(x-data[0].x)/xR*(W-PAD*2)+PAD, H-PAD-(y-mn)/yR*(H-PAD*2)];

  const pathStr=data.map((d,i)=>{const [px,py]=toSVG(d.x,d.loss);return `${i===0?'M':'L'}${px.toFixed(1)} ${py.toFixed(1)}`;}).join(' ');

  const COLORS=['#22d3ee','#4ade80','#a78bfa','#f87171'];

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'6px',border:'1px solid #1e293b',display:'block'}}>
      {/* Grid */}
      {[0,0.25,0.5,0.75,1].map(t=>{
        const y=PAD+(1-t)*(H-PAD*2);
        return <line key={t} x1={PAD} y1={y} x2={W-PAD} y2={y} stroke="#1e293b" strokeWidth={1} strokeDasharray="3 3"/>;
      })}
      {/* Loss curve */}
      <path d={pathStr} fill="none" stroke="#fb923c" strokeWidth={2.5}/>
      <path d={`${pathStr} L${(W-PAD)} ${H-PAD} L${PAD} ${H-PAD} Z`} fill="rgba(251,146,60,0.07)"/>
      {/* Optimizer paths */}
      {paths&&paths.map((path,pi)=>{
        const color=COLORS[pi%COLORS.length];
        const pts=path.positions.map((pos,i)=>{
          const lossAtPos=data.reduce((best,d)=>Math.abs(d.x-pos)<Math.abs(best.x-pos)?d:best,data[0]).loss;
          const [px,py]=toSVG(pos,lossAtPos);
          return `${i===0?'M':'L'}${px.toFixed(1)} ${py.toFixed(1)}`;
        }).join(' ');
        return <path key={pi} d={pts} fill="none" stroke={color} strokeWidth={1.5} strokeDasharray="6 3" opacity={0.8}/>;
      })}
      {/* Minimum marker */}
      {(()=>{const minPt=data.reduce((b,d)=>d.loss<b.loss?d:b,data[0]);const [px,py]=toSVG(minPt.x,minPt.loss);return(<g><circle cx={px} cy={py} r={5} fill="#4ade80" stroke="#000" strokeWidth={1}/><text x={px+8} y={py} dominantBaseline="middle" style={{fontSize:'9px',fill:'#4ade80',fontFamily:'JetBrains Mono',fontWeight:700}}>MIN</text></g>);})()}
      {/* Axes */}
      <line x1={PAD} y1={PAD} x2={PAD} y2={H-PAD} stroke="#334155" strokeWidth={1}/>
      <line x1={PAD} y1={H-PAD} x2={W-PAD} y2={H-PAD} stroke="#334155" strokeWidth={1}/>
      <text x={W/2} y={H-6} textAnchor="middle" style={{fontSize:'9px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>parameter w</text>
      <text x={10} y={H/2} textAnchor="middle" dominantBaseline="middle" transform={`rotate(-90,10,${H/2})`} style={{fontSize:'9px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>loss</text>
    </svg>
  );
}

export default function TrainingLab() {
  const [tab,setTab]=useState('loss-curves');
  const [logs,setLogs]=useState([{ts:'00:00:00',msg:'Training Dynamics Lab — explore how training hyperparameters affect convergence.',t:'info'}]);
  const log=(msg,t='info')=>setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);
  const [running,setRunning]=useState(false);

  // Loss curves
  const [lr,setLr]=useState(0.01);
  const [epochs,setEpochs]=useState(200);
  const [batchSize,setBatchSize]=useState(32);
  const [optimizer,setOptimizer]=useState('adam');
  const [sched,setSched]=useState('cosine');
  const [curves,setCurves]=useState(null);

  // LR Schedule
  const [schedLr,setSchedLr]=useState(0.1);
  const [schedEpochs,setSchedEpochs]=useState(200);
  const [schedResult,setSchedResult]=useState(null);

  // Landscape
  const [sharpness,setSharpness]=useState(1.0);
  const [landscape,setLandscape]=useState(null);

  // Batch effects
  const [batchResult,setBatchResult]=useState(null);

  // Gradient health
  const [ghResult,setGhResult]=useState(null);
  const [clipVal,setClipVal]=useState(1.0);
  const [ghEpochs,setGhEpochs]=useState(150);

  // Compare
  const [cmpResult,setCmpResult]=useState(null);

  const run = (fn) => { setRunning(true); setTimeout(()=>{fn();setRunning(false);},20); };

  const runCurves = () => run(()=>{
    const data=simTraining({lr0:lr,epochs,batchSize,sched,optimizer});
    setCurves(data);
    log(`lr=${lr} batch=${batchSize} opt=${optimizer} sched=${sched} | final loss=${data.at(-1)?.loss} val=${data.at(-1)?.val}`,'ok');
  });

  const runSchedules = () => run(()=>{
    const scheds=Object.keys(LR_SCHEDULES);
    const results=scheds.map(s=>({name:s,data:Array.from({length:schedEpochs},(_,e)=>({epoch:e,lr:+LR_SCHEDULES[s](e,schedLr,schedEpochs).toFixed(6)}))}));
    setSchedResult(results);
    log(`LR schedules computed: ${scheds.join(', ')}`,'ok');
  });

  const runLandscape = () => run(()=>{
    const data=lossLandscape([-3,3],120,sharpness);
    // Simulate optimizer paths
    const sgdPath=[]; const adamPath=[]; let xs=-2.5, xa=-2.5;
    for(let i=0;i<40;i++){
      const gx=0.6*xs*sharpness+0.6*Math.cos(xs*3)*sharpness;
      const ga=0.6*xa*sharpness+0.6*Math.cos(xa*3)*sharpness;
      xs-=0.05*gx; xa-=0.03*ga/(Math.abs(ga)+0.1);
      sgdPath.push(xs); adamPath.push(xa);
    }
    setLandscape({data,paths:[{name:'SGD',positions:sgdPath},{name:'Adam',positions:adamPath}]});
    log(`Loss landscape sharpness=${sharpness} | global min near w=0`,'ok');
  });

  const runBatchEffects = () => run(()=>{
    const sizes=[1,4,16,64,256,1024];
    const results=sizes.map(bs=>{
      const d=simTraining({lr0:0.01,epochs:100,batchSize:bs,sched:'constant',optimizer:'sgd',noise:1});
      const noise=d.slice(10).map((p,i,arr)=>i>0?Math.abs(p.loss-arr[i-1].loss):0).reduce((s,v)=>s+v,0)/d.length;
      return {bs:`B=${bs}`,finalLoss:d.at(-1)?.loss,noise:+noise.toFixed(4),finalVal:d.at(-1)?.val,gap:+(d.at(-1)?.val-d.at(-1)?.loss).toFixed(4)};
    });
    setBatchResult(results);
    log(`Batch effects: ${sizes.join(',')} | large batch generalizes worse`,'ok');
  });

  const runGradHealth = () => run(()=>{
    const layers=5; const pts=[];
    for(let e=0;e<ghEpochs;e++){
      const pt={epoch:e};
      for(let l=0;l<layers;l++){
        const base=Math.pow(0.7,l)*Math.exp(-e*0.01)+randn()*0.01;
        const raw=Math.max(0.001,base);
        const clipped=Math.min(raw,clipVal);
        pt[`L${l+1}_raw`]=+raw.toFixed(5);
        pt[`L${l+1}_clip`]=+clipped.toFixed(5);
      }
      pt.gradNorm=+Math.sqrt(Array.from({length:layers},(_,l)=>(pt[`L${l+1}_raw`]??0)**2).reduce((s,v)=>s+v,0)).toFixed(5);
      pt.clippedNorm=+Math.min(pt.gradNorm,clipVal).toFixed(5);
      if(e%2===0) pts.push(pt);
    }
    setGhResult(pts);
    log(`Gradient health: ${layers} layers, clip=${clipVal}, epochs=${ghEpochs}`,'ok');
  });

  const runCompare = () => run(()=>{
    const cfgs=[
      {name:'SGD lr=0.1',params:{lr0:0.1,optimizer:'sgd',sched:'constant'},color:P[5]},
      {name:'SGD+Warmup',params:{lr0:0.1,optimizer:'sgd',sched:'warmup_cos'},color:P[1]},
      {name:'Adam lr=0.01',params:{lr0:0.01,optimizer:'adam',sched:'constant'},color:P[2]},
      {name:'Adam+Cosine',params:{lr0:0.01,optimizer:'adam',sched:'cosine'},color:P[3]},
      {name:'Adam+OneCycle',params:{lr0:0.01,optimizer:'adam',sched:'one_cycle'},color:P[4]},
      {name:'Large Batch 512',params:{lr0:0.05,optimizer:'adam',sched:'cosine',batchSize:512},color:P[0]},
    ];
    const results=cfgs.map(c=>({...c,data:simTraining({epochs:200,batchSize:32,...c.params})}));
    const chart=results[0].data.map((p,i)=>{const pt={epoch:p.epoch};results.forEach(r=>{pt[r.name]=r.data[i]?.val;});return pt;});
    const finals=results.map(r=>({name:r.name,color:r.color,finalVal:r.data.at(-1)?.val,finalLoss:r.data.at(-1)?.loss})).sort((a,b)=>a.finalVal-b.finalVal);
    setCmpResult({chart,finals});
    log(`Compare done | winner: ${finals[0].name} val=${finals[0].finalVal}`,'ok');
  });

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── LOSS CURVES ── */}
      {tab==='loss-curves'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="TRAINING CONFIG" accent={AC}>
              <Sl label="Learning Rate" value={lr} min={0.0001} max={0.5} step={0.0001} onChange={setLr} color="#fb923c"/>
              <Sl label="Batch Size" value={batchSize} min={1} max={512} step={1} onChange={setBatchSize} color="#22d3ee"/>
              <Sl label="Epochs" value={epochs} min={50} max={500} step={10} onChange={setEpochs} color="#fbbf24"/>
              <Sel label="OPTIMIZER" value={optimizer} onChange={setOptimizer} options={[{value:'adam',label:'Adam'},{value:'sgd',label:'SGD'},{value:'sgd_m',label:'SGD+Momentum'},{value:'rmsprop',label:'RMSProp'}]}/>
              <Sel label="LR SCHEDULE" value={sched} onChange={setSched} options={Object.keys(LR_SCHEDULES).map(v=>({value:v,label:v.replace(/_/g,' ').toUpperCase()}))}/>
              <Btn onClick={runCurves} disabled={running} color={AC} style={{width:'100%'}}>{running?'RUNNING…':'TRAIN →'}</Btn>
            </Panel>
            <LogPanel logs={logs} height={140}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {curves
              ? <>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
                    <Metric label="Final Train Loss" value={curves.at(-1)?.loss} color={AC}/>
                    <Metric label="Final Val Loss" value={curves.at(-1)?.val} color="#f87171"/>
                    <Metric label="Final LR" value={curves.at(-1)?.lr} color="#fbbf24"/>
                    <Metric label="Gen Gap" value={+(curves.at(-1)?.val-curves.at(-1)?.loss).toFixed(4)} color={+(curves.at(-1)?.val-curves.at(-1)?.loss)>0.1?'#f87171':'#4ade80'}/>
                  </div>
                  <Panel title="TRAIN / VAL LOSS" accent={AC}>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={curves}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Legend/>
                        <Line type="monotone" dataKey="loss" stroke="#22d3ee" strokeWidth={2} dot={false} name="Train"/>
                        <Line type="monotone" dataKey="val" stroke="#fb923c" strokeWidth={2} dot={false} strokeDasharray="5 3" name="Val"/>
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                    <Panel title="LR SCHEDULE" accent="#fbbf24">
                      <ResponsiveContainer width="100%" height={130}>
                        <AreaChart data={curves}>
                          <defs><linearGradient id="lrGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#fbbf24" stopOpacity={0.3}/><stop offset="95%" stopColor="#fbbf24" stopOpacity={0}/></linearGradient></defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                          <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                          <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                          <Tooltip/>
                          <Area type="monotone" dataKey="lr" stroke="#fbbf24" fill="url(#lrGrad)" strokeWidth={2} dot={false} name="LR"/>
                        </AreaChart>
                      </ResponsiveContainer>
                    </Panel>
                    <Panel title="GRADIENT NORM" accent="#a78bfa">
                      <ResponsiveContainer width="100%" height={130}>
                        <LineChart data={curves}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                          <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                          <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                          <Tooltip/>
                          <Line type="monotone" dataKey="gradNorm" stroke="#a78bfa" strokeWidth={2} dot={false} name="‖∇‖"/>
                        </LineChart>
                      </ResponsiveContainer>
                    </Panel>
                  </div>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Configure and train</div>
            }
          </div>
        </div>
      )}

      {/* ── LR SCHEDULE ── */}
      {tab==='lr-schedule'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="SCHEDULE PARAMS" accent="#fbbf24">
              <Sl label="Initial LR" value={schedLr} min={0.001} max={1.0} step={0.001} onChange={setSchedLr} color="#fbbf24"/>
              <Sl label="Total Epochs" value={schedEpochs} min={50} max={500} step={10} onChange={setSchedEpochs} color="#22d3ee"/>
              <Btn onClick={runSchedules} disabled={running} color="#fbbf24" style={{width:'100%'}}>{running?'…':'PLOT SCHEDULES →'}</Btn>
              <div style={{marginTop:'10px',fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>
                Warmup+Cosine is the most common for transformers. One-cycle for fast training. Cyclic for SGD.
              </div>
            </Panel>
            {schedResult&&schedResult.map((r,i)=>(
              <div key={r.name} style={{display:'flex',justifyContent:'space-between',padding:'5px 8px',background:'var(--deep)',borderRadius:'2px',fontSize:'9px',borderLeft:`2px solid ${P[i%P.length]}`}}>
                <span style={{color:P[i%P.length],fontFamily:'JetBrains Mono',fontWeight:700}}>{r.name}</span>
                <span style={{color:'var(--t3)'}}>final: {r.data.at(-1)?.lr.toFixed(5)}</span>
              </div>
            ))}
          </div>
          <div>
            {schedResult
              ? <Panel title="ALL LR SCHEDULES" accent="#fbbf24">
                  <ResponsiveContainer width="100%" height={380}>
                    <LineChart data={schedResult[0].data.map((p,i)=>{const pt={epoch:p.epoch};schedResult.forEach(r=>{pt[r.name]=r.data[i]?.lr;});return pt;})}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <Tooltip contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:10}}/>
                      <Legend/>
                      {schedResult.map((r,i)=><Line key={r.name} type="monotone" dataKey={r.name} stroke={P[i%P.length]} strokeWidth={2} dot={false}/>)}
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Plot schedules</div>
            }
          </div>
        </div>
      )}

      {/* ── LANDSCAPE ── */}
      {tab==='landscape'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="LANDSCAPE CONTROLS" accent="#a78bfa">
              <Sl label="Sharpness" value={sharpness} min={0.1} max={5} step={0.1} onChange={setSharpness} color="#a78bfa"/>
              <Btn onClick={runLandscape} disabled={running} color="#a78bfa" style={{width:'100%'}}>{running?'…':'COMPUTE →'}</Btn>
              <div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>
                High sharpness = sharp minima = worse generalization. Large-batch training tends to find sharper minima.
              </div>
            </Panel>
            {landscape&&(
              <Panel title="KEY INSIGHT" accent="#fbbf24">
                <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.7}}>
                  SGD navigates the loss surface more noisily, often landing in flat minima (better generalization). Adam converges faster but can find sharper minima.
                </div>
              </Panel>
            )}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {landscape
              ? <>
                  <Panel title="LOSS LANDSCAPE — 1D SLICE THROUGH WEIGHT SPACE" accent="#a78bfa">
                    <LossLandscapeSVG data={landscape.data} paths={landscape.paths} sharpness={sharpness}/>
                    <div style={{display:'flex',gap:'14px',marginTop:'8px',fontSize:'9px',color:'var(--t3)'}}>
                      <span><span style={{color:'#fb923c'}}>──</span> Loss landscape</span>
                      <span><span style={{color:'#22d3ee'}}>╌╌</span> SGD path</span>
                      <span><span style={{color:'#4ade80'}}>╌╌</span> Adam path</span>
                      <span><span style={{color:'#4ade80'}}>●</span> Global minimum</span>
                    </div>
                  </Panel>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
                    {[['Global Min',landscape.data.reduce((b,d)=>d.loss<b.loss?d:b,landscape.data[0]).x,'#4ade80'],['Min Loss',landscape.data.reduce((b,d)=>d.loss<b.loss?d:b,landscape.data[0]).loss.toFixed(4),'#fb923c'],['Sharpness',sharpness.toFixed(1)+'×',sharpness>3?'#f87171':sharpness>1.5?'#fbbf24':'#4ade80']].map(([k,v,c])=>(
                      <Metric key={k} label={k} value={v} color={c}/>
                    ))}
                  </div>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Adjust sharpness and compute</div>
            }
          </div>
        </div>
      )}

      {/* ── BATCH EFFECTS ── */}
      {tab==='batch-effects'&&(
        <div style={{display:'grid',gridTemplateColumns:'180px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="BATCH SIZE" accent="#22d3ee">
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px',lineHeight:1.6}}>Compares B=1,4,16,64,256,1024 all else equal.</div>
              <Btn onClick={runBatchEffects} disabled={running} color="#22d3ee" style={{width:'100%'}}>{running?'…':'RUN ALL →'}</Btn>
            </Panel>
            <LogPanel logs={logs} height={200}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {batchResult
              ? <>
                  <Panel title="FINAL VAL LOSS BY BATCH SIZE" accent="#22d3ee">
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={batchResult}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="bs" tick={{fontSize:10,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Bar dataKey="finalVal" name="Final Val Loss" radius={[3,3,0,0]}>
                          {batchResult.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={0.85}/>)}
                        </Bar>
                        <Bar dataKey="finalLoss" name="Final Train Loss" radius={[3,3,0,0]} opacity={0.5}>
                          {batchResult.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={0.4}/>)}
                        </Bar>
                        <Legend/>
                      </BarChart>
                    </ResponsiveContainer>
                  </Panel>
                  <Panel title="GENERALIZATION GAP — LARGE BATCH SHARP MINIMA" accent="#f87171">
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={batchResult}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="bs" tick={{fontSize:10,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Bar dataKey="gap" name="Val-Train Gap" radius={[3,3,0,0]}>
                          {batchResult.map((d,i)=><Cell key={i} fill={d.gap>0.1?'#f87171':d.gap>0.05?'#fbbf24':'#4ade80'} opacity={0.9}/>)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Panel>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'10px',fontFamily:'JetBrains Mono'}}>
                    <thead><tr style={{background:'var(--deep)'}}>
                      {['Batch','Train Loss','Val Loss','Gap','Noise'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {batchResult.map((r,i)=>(
                        <tr key={i} style={{borderBottom:'1px solid var(--b0)'}}>
                          <td style={{padding:'6px 10px',color:P[i%P.length],fontWeight:700}}>{r.bs}</td>
                          <td style={{padding:'6px 10px',color:'#22d3ee'}}>{r.finalLoss}</td>
                          <td style={{padding:'6px 10px',color:'#fb923c'}}>{r.finalVal}</td>
                          <td style={{padding:'6px 10px',color:r.gap>0.1?'#f87171':r.gap>0.05?'#fbbf24':'#4ade80'}}>{r.gap}</td>
                          <td style={{padding:'6px 10px',color:'#a78bfa'}}>{r.noise}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Run batch comparison</div>
            }
          </div>
        </div>
      )}

      {/* ── GRADIENT HEALTH ── */}
      {tab==='gradient-health'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="GRAD CONTROLS" accent="#a78bfa">
              <Sl label="Clip Value" value={clipVal} min={0.1} max={5} step={0.1} onChange={setClipVal} color="#a78bfa"/>
              <Sl label="Epochs" value={ghEpochs} min={50} max={300} step={10} onChange={setGhEpochs} color="#22d3ee"/>
              <Btn onClick={runGradHealth} disabled={running} color="#a78bfa" style={{width:'100%'}}>{running?'…':'SIMULATE →'}</Btn>
              <div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>
                Gradient clipping prevents explosions. Vanishing = earlier layers learn nothing. Watch Layer 1 vs Layer 5.
              </div>
            </Panel>
            <LogPanel logs={logs} height={160}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {ghResult
              ? <>
                  <Panel title="GRADIENT NORMS — EACH LAYER OVER TRAINING (log scale)" accent="#a78bfa">
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={ghResult}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}} scale="log" domain={[0.0001,2]}/>
                        <Tooltip/>
                        <Legend/>
                        <ReferenceLine y={clipVal} stroke="#fbbf24" strokeDasharray="5 3" label={{value:`clip=${clipVal}`,fill:'#fbbf24',fontSize:8}}/>
                        {Array.from({length:5},(_,i)=>(
                          <Line key={i} type="monotone" dataKey={`L${i+1}_raw`} stroke={P[i%P.length]} strokeWidth={1.5} dot={false} name={`Layer ${i+1}`}/>
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                  <Panel title="GLOBAL GRADIENT NORM — RAW vs CLIPPED" accent="#fbbf24">
                    <ResponsiveContainer width="100%" height={150}>
                      <LineChart data={ghResult}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Legend/>
                        <ReferenceLine y={clipVal} stroke="#fbbf24" strokeDasharray="5 3"/>
                        <Line type="monotone" dataKey="gradNorm" stroke="#f87171" strokeWidth={2} dot={false} name="Raw ‖∇‖"/>
                        <Line type="monotone" dataKey="clippedNorm" stroke="#4ade80" strokeWidth={2} dot={false} name="Clipped ‖∇‖"/>
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Simulate gradient health</div>
            }
          </div>
        </div>
      )}

      {/* ── COMPARE ── */}
      {tab==='compare'&&(
        <div style={{display:'grid',gridTemplateColumns:'180px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="ALL VS ALL" accent={AC}>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px',lineHeight:1.6}}>6 training configs on the same task. Find the best setup.</div>
              <Btn onClick={runCompare} disabled={running} color={AC} style={{width:'100%'}}>{running?'RUNNING…':'RUN ALL →'}</Btn>
            </Panel>
            <LogPanel logs={logs} height={200}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {cmpResult
              ? <>
                  <Panel title="VAL LOSS — ALL CONFIGS" accent={AC}>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={cmpResult.chart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Legend/>
                        {cmpResult.finals.map(r=><Line key={r.name} type="monotone" dataKey={r.name} stroke={r.color} strokeWidth={2} dot={false}/>)}
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                  <Panel title="FINAL LEADERBOARD" accent={AC}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                      <thead><tr style={{background:'var(--deep)'}}>
                        {['Rank','Config','Train Loss','Val Loss'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {cmpResult.finals.map((r,i)=>(
                          <tr key={r.name} style={{borderBottom:'1px solid var(--b0)'}}>
                            <td style={{padding:'7px 10px',color:P[i%P.length],fontFamily:'Syne',fontWeight:800,fontSize:'16px'}}>{i+1}</td>
                            <td style={{padding:'7px 10px',color:r.color,fontWeight:600,fontSize:'11px'}}>{r.name}</td>
                            <td style={{padding:'7px 10px',color:'#22d3ee',fontFamily:'JetBrains Mono'}}>{r.finalLoss}</td>
                            <td style={{padding:'7px 10px',color:'#fb923c',fontFamily:'JetBrains Mono'}}>{r.finalVal}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Panel>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Run all configs to find the winner</div>
            }
          </div>
        </div>
      )}
    </div>
  );
}
